import React, { useMemo, ReactNode } from 'react';
import { message } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { PartItem, PartSlot } from '@partsbadger/library';
import { ALLOWED_FILES } from '@partsbadger/utils';
import { IPartFileRecord } from '@partsbadger/types';
import { observer } from 'mobx-react';

export type PartFileType = '3D File' | '2D File';

interface SwapFilePayload {
    fromIndex: number;
    toIndex: number;
    fromType: PartFileType;
    toType: PartFileType;
    file: File;
}

interface AddFilePayload {
    index: number;
    type: PartFileType;
    file: File;
}

interface MatchPartFilesToolProps {
    parts: IPartFileRecord[];
    onRemovePart: (part: IPartFileRecord) => void;
    onRemoveFile: (part: IPartFileRecord, fileType: PartFileType, file?: File) => void;
    onSwapFile: (payload: SwapFilePayload) => void;
    onAddFile: (payload: AddFilePayload) => void;
    columnHeaders?: Partial<Record<PartFileType, ReactNode>>;
    columnOrder?: PartFileType[];
    allowedFiles?: { Files3D: string[]; Files2D: string[] };
    onInvalidFile?: (type: PartFileType, file: File) => void;
    rowClassName?: string;
    rowStyle?: React.CSSProperties;
    containerClassName?: string;
    columnClassName?: string;
    errorDuration?: number;
    renderRemovePartIcon?: (part: IPartFileRecord) => ReactNode;
}

const MatchPartFilesTool: React.FC<MatchPartFilesToolProps> = ({
    parts,
    onRemovePart,
    onRemoveFile,
    onSwapFile,
    onAddFile,
    columnHeaders,
    columnOrder,
    allowedFiles = ALLOWED_FILES,
    onInvalidFile,
    rowClassName,
    rowStyle,
    containerClassName,
    columnClassName,
    errorDuration = 4,
    renderRemovePartIcon,
}) => {
    const columns: PartFileType[] = columnOrder && columnOrder.length ? columnOrder : ['3D File', '2D File'];
    const acceptedTypes: PartFileType[] = ['3D File', '2D File'];

    const allowed3D = useMemo(() => (allowedFiles.Files3D || []).map(ext => ext.toLowerCase()), [allowedFiles]);
    const allowed2D = useMemo(() => (allowedFiles.Files2D || []).map(ext => ext.toLowerCase()), [allowedFiles]);

    const handleInvalidFile = (type: PartFileType, file: File) => {
        if (onInvalidFile) {
            onInvalidFile(type, file);
        } else if (type === '3D File' || type === '2D File') {
            message.error(
                `The file type is not allowed for the ${type === '3D File' ? '3D' : '2D'} category.`,
                errorDuration
            );
        }
    };

    const isAllowed = (filename: string, type: PartFileType) => {
        const extension = filename.split('.').pop()?.toLowerCase();
        if (!extension) {
            return false;
        }
        return type === '3D File' ? allowed3D.includes(extension) : allowed2D.includes(extension);
    };

    if (!parts.length) {
        return null;
    }

    const renderHeader = (type: PartFileType) =>
        columnHeaders?.[type] ?? <div className="text-xl font-bold">{type}s</div>;

    const renderRemoveIcon = (part: IPartFileRecord) =>
        renderRemovePartIcon?.(part) ?? (
            <CloseCircleOutlined
                style={{ position: 'absolute', right: -10, top: 5, cursor: 'pointer', color: '#d92923', fontSize: 18 }}
                onClick={() => onRemovePart(part)}
            />
        );

    const renderSlotContent = (part: IPartFileRecord, index: number, type: PartFileType) => {
        const file = part[type];
        if (!file) {
            return null;
        }

        return <PartItem index={index} file={file} type={type} handleRemove={() => onRemoveFile(part, type)} />;
    };

    const renderSlot = (part: IPartFileRecord, index: number, type: PartFileType) => (
        <div
            key={`${part.uid}-${type}`}
            className={columnClassName}
            style={{ flex: 1, marginLeft: 8, marginRight: 8, textAlign: 'center' }}
        >
            <PartSlot
                index={index}
                accept={acceptedTypes}
                lastDroppedItem={renderSlotContent(part, index, type)}
                onDrop={item => {
                    if (!isAllowed(item.file.name, type)) {
                        handleInvalidFile(type, item.file);
                        return;
                    }
                    onSwapFile({
                        fromIndex: item.index,
                        toIndex: index,
                        fromType: item.type as PartFileType,
                        toType: type,
                        file: item.file,
                    });
                }}
                onDropNew={files => {
                    if (!files.length) {
                        return;
                    }
                    const file = files[files.length - 1];
                    if (!file) {
                        return;
                    }
                    if (!isAllowed(file.name, type)) {
                        handleInvalidFile(type, file);
                        return;
                    }
                    onAddFile({
                        index,
                        type,
                        file,
                    });
                }}
            />
        </div>
    );

    return (
        <DndProvider backend={HTML5Backend}>
            <div className={containerClassName}>
                <div className="flex flex-row mb-4">
                    {columns.map(type => (
                        <div
                            key={`header-${type}`}
                            className={columnClassName}
                            style={{ flex: 1, marginLeft: 8, marginRight: 8, textAlign: 'center' }}
                        >
                            {renderHeader(type)}
                        </div>
                    ))}
                </div>
                {parts.map((part, index) => (
                    <div
                        key={`${part.uid}-${index}`}
                        className={rowClassName ?? 'row-associate-parts'}
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: 5,
                            padding: 10,
                            position: 'relative',
                            ...rowStyle,
                        }}
                    >
                        {renderRemoveIcon(part)}
                        {columns.map(type => renderSlot(part, index, type))}
                    </div>
                ))}
            </div>
        </DndProvider>
    );
};

export default observer(MatchPartFilesTool);
